import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box } from '@mui/material';

// Components
import LandingPage from './components/LandingPage';
import Navbar from './components/Navbar';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import ChatConstraints from "./components/ChatConstraints";
import ScheduleView from "./components/ScheduleView";
import ConstraintsView from "./components/ConstraintsView";

// Context
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

// Component to conditionally render Navbar and layout
function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isLoginPage && <Navbar />}
      <Routes>
        {/* Landing page - root route */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Public routes - Login should not be wrapped in Container */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes - All use /app prefix to match Firebase rewrite */}
        {/* Home route - /app */}
        <Route path="/app" element={
          <Container maxWidth="xl" sx={{ mt: 3, mb: 3, flex: 1 }}>
            <ProtectedRoute requiredRole="admin">
              <ChatConstraints />
            </ProtectedRoute>
          </Container>
        } />
        
        {/* Schedule route - /app/schedule */}
        <Route path="/app/schedule" element={
          <Container maxWidth="xl" sx={{ mt: 3, mb: 3, flex: 1 }}>
            <ProtectedRoute requiredRole="admin">
              <ScheduleView />
            </ProtectedRoute>
          </Container>
        } />
        
        {/* Constraints route - /app/constraints */}
        <Route path="/app/constraints" element={
          <Container maxWidth="xl" sx={{ mt: 3, mb: 3, flex: 1 }}>
            <ProtectedRoute>
              <ConstraintsView />
            </ProtectedRoute>
          </Container>
        } />
        
        {/* Catch-all: redirect unknown routes to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
